// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, InlineRender } from 'utils/reduxFormWrapper';
import { PlainSelectInput } from 'shared/containers/portal_layout/HForm';
import { newConditional, newConditionalGroup } from './utils';
import { Conditional, ViewConditional } from './Conditional';
import logProps from 'utils/logProps';
/* eslint-disable no-unused-vars */
import {
  ConditionalGroup as ConditionalGroupType,
  RulableField,
} from './types';
/* eslint-enable no-unused-vars */

const logicalOperators = [
  { label: 'any', value: 'OR' },
  { label: 'all', value: 'AND' },
];

/** @typedef {{
  attributes: ConditionalGroupType,
  rulableFields: RulableField[],
}} ViewConditionalGroupPropsType */

/** @type { React.FC<ViewConditionalGroupPropsType> } */
const ViewConditionalGroup = ({ attributes, rulableFields }) => {
  const conditionals = (attributes.conditionals || []).map((conditional) => (
    <ViewConditional
      key={conditional.id}
      attributes={conditional}
      rulableFields={rulableFields}
    />
  ));

  const children = (attributes.children || []).map((conditionalGroup) => (
    <ViewConditionalGroup
      key={conditionalGroup.id}
      attributes={conditionalGroup}
      rulableFields={rulableFields}
    />
  ));
  const allPieces = conditionals.concat(children);
  const withDelimiter = allPieces.map((piece, idx) =>
    idx < allPieces.length - 1
      ? [piece, <span>{attributes.operator}</span>]
      : piece
  );

  return <span>({withDelimiter})</span>;
};

const ConditionalGroups = ({ fields, ...rest }) => (
  <div>
    {fields.map((fieldName, idx) => (
      <ConditionalGroup
        {...rest}
        name={fieldName}
        key={idx}
        removable
        remove={() => fields.remove(idx)}
      />
    ))}
  </div>
);

const Conditionals = ({ fields, ...rest }) => (
  <div>
    {fields.map((fieldName, idx) => (
      <Conditional
        key={idx}
        remove={() => fields.remove(idx)}
        removable
        {...rest}
        name={fieldName}
      />
    ))}
  </div>
);

const ConditionalGroup = logProps(
  'ConditionalGroup',
  ({ name, removable, remove, ...rest }) => (
    <div className="conditional-group">
      <div className="conditional-group-controls">
        Match
        <div style={{ display: 'inline-block', margin: '0 0.5em', width: 64 }}>
          <PlainSelectInput
            style={{ width: 64 }}
            name={`${name}.operator`}
            options={logicalOperators}
            data-spec-id="conditional-group-operator"
          />
        </div>
        of the following conditions:
        <FieldArray
          name={`${name}.conditionals`}
          component={InlineRender}
          render={({ fields }) => (
            <button
              style={{ marginLeft: '0.5em' }}
              type="button"
              className="btn btn-mini btn-success"
              onClick={() => fields.push(newConditional())}
            >
              <i className="icon icon-plus-sign icon-white" /> Add Condition
            </button>
          )}
        />
        <FieldArray
          name={`${name}.children`}
          component={InlineRender}
          render={({ fields }) => (
            <button
              style={{ marginLeft: '0.5em' }}
              type="button"
              className="btn btn-mini btn-success"
              onClick={() => fields.push(newConditionalGroup())}
              data-spec-id="add-group-button"
            >
              <i className="icon icon-plus-sign icon-white" /> Add Child Group
            </button>
          )}
        />
        {removable && (
          <button
            type="button"
            className="btn btn-mini btn-danger"
            onClick={remove}
            data-spec-id="remove-group-button"
          >
            <i className="icon icon-minus-sign icon-white" /> Remove this Group
          </button>
        )}
      </div>
      <div data-spec-id="group-conditions">
        <FieldArray
          name={`${name}.conditionals`}
          component={Conditionals}
          {...rest}
        />
        <FieldArray
          name={`${name}.children`}
          component={ConditionalGroups}
          {...rest}
        />
      </div>
    </div>
  )
);

ConditionalGroup.propTypes = {
  rulableFields: PropTypes.array.isRequired,
};

export { ConditionalGroup, ViewConditionalGroup };
